import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../environments/environments';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';

export interface DecodedJwtPayload {
  nameid?: string;
  unique_name?: string;
  email?: string;
  role?: string;
  admin?: boolean | string;
  isAdmin?: boolean | string;
  root?: boolean | string;
  isRoot?: boolean | string;
  exp?: number;
  nbf?: number;
  iat?: number;
  [key: string]: any;
}

export interface WorkforceUserSession {
  id: string;
  name: string;
  username: string;
  email: string;
  role: 'Admin' | 'Diretor' | 'Gerente Regional' | 'Gestor' | 'Equipe de Ponto';
  admin: boolean;
  root: boolean;
  allowedBranches: string[];
  token: string;
}

/** Response of POST api/v1/auth/login (Workforce.Api AuthModel). */
interface ApiAuthResponse {
  accessToken: string;
  expiresIn: number;
  user: {
    uuid: string;
    name: string;
    username: string;
    email: string;
    role: string;
    mustChangePassword: boolean;
    branchCodes: string[];
  };
}

/** Maps Workforce.Api UserRole values to the profiles used by the UI. */
function toSessionRole(apiRole: string): WorkforceUserSession['role'] {
  switch (apiRole) {
    case 'Admin':
      return 'Admin';
    case 'Director':
    case 'Diretor':
      return 'Diretor';
    case 'RegionalManager':
    case 'Gerente Regional':
      return 'Gerente Regional';
    case 'TimecardTeam':
      return 'Equipe de Ponto';
    default:
      return 'Gestor';
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  readonly user$: BehaviorSubject<any> = new BehaviorSubject<any>(this.getUser());
  readonly token$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(this.getToken());
  private readonly _currentUser = signal<WorkforceUserSession | null>(this.getUser());
  readonly currentUser = this._currentUser.asReadonly();
  readonly isLoggedSignal = computed(() => !!this._currentUser());

  getToken(): string | null {
    try {
      const token = sessionStorage.getItem('token');
      return token && token.trim().length > 0 ? token.trim() : null;
    } catch {
      return null;
    }
  }

  getUser(): WorkforceUserSession | null {
    try {
      const userStr = sessionStorage.getItem('user');
      if (!userStr) return null;
      const parsed = JSON.parse(userStr);
      return parsed && parsed.data ? parsed.data : parsed;
    } catch {
      return null;
    }
  }

  getDecodedToken(): DecodedJwtPayload | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      return jwtDecode<DecodedJwtPayload>(token);
    } catch {
      return null;
    }
  }

  isTokenExpired(token?: string | null): boolean {
    const targetToken = token || this.getToken();
    if (!targetToken) return true;

    try {
      const decoded = jwtDecode<DecodedJwtPayload>(targetToken);
      if (!decoded || !decoded.exp) return true;
      const currentTimeInSeconds = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTimeInSeconds + 10;
    } catch {
      return true;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return !this.isTokenExpired(token);
  }

  isRoot(): boolean {
    if (!this.isAuthenticated()) return false;
    const user = this.getUser();
    return user?.root === true || user?.role === 'Admin';
  }

  isAdminOrRoot(): boolean {
    if (!this.isAuthenticated()) return false;
    const user = this.getUser();
    if (user && (user.root === true || user.admin === true || user.role === 'Admin')) {
      return true;
    }
    const payload = this.getDecodedToken();
    if (payload) {
      const role = String(payload.role || '').toLowerCase();
      if (role === 'admin' || role === 'root' || payload.admin === true || payload.root === true) {
        return true;
      }
    }
    return false;
  }

  isTimecardOnly(): boolean {
    if (!this.isAuthenticated() || this.isAdminOrRoot()) return false;
    const user = this.getUser();
    if (user?.role === 'Equipe de Ponto') return true;
    const payload = this.getDecodedToken();
    const role = String(payload?.role || '').toLowerCase();
    return role === 'timecardteam' || role === 'equipe de ponto';
  }

  canManageSchedule(): boolean {
    if (!this.isAuthenticated()) return false;
    return !this.isTimecardOnly();
  }

  isTimecardTeam(): boolean {
    if (this.isAdminOrRoot()) return true;
    const user = this.getUser();
    return user?.role === 'Equipe de Ponto';
  }

  isBranchManager(): boolean {
    if (!this.isAuthenticated()) return false;
    const user = this.getUser();
    return (
      user?.role === 'Gestor' ||
      user?.role === 'Gerente Regional' ||
      user?.role === 'Diretor' ||
      this.isAdminOrRoot()
    );
  }

  canViewRegionalCoverage(): boolean {
    if (!this.isAuthenticated()) return false;
    if (this.isAdminOrRoot()) return true;
    const user = this.getUser();
    if (user?.role === 'Gerente Regional' || user?.role === 'Diretor') {
      return true;
    }
    const payload = this.getDecodedToken();
    const role = String(payload?.role || '').toLowerCase();
    return (
      role === 'regionalmanager' ||
      role === 'gerente regional' ||
      role === 'director' ||
      role === 'diretor'
    );
  }

  sanitizeReturnUrl(url: string | null | undefined): string {
    if (!url || typeof url !== 'string') return '/home';
    const trimmed = url.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('\\') || trimmed.includes(':')) {
      return '/home';
    }
    if (trimmed === '/' || trimmed === '/login') {
      return '/home';
    }
    return trimmed;
  }

  onRequestLogin(body: { user: string; password: string }): Observable<WorkforceUserSession> {
    return this.http
      .post<ApiAuthResponse>(`${environment.WORKFORCE_API}auth/login`, {
        usernameOrEmail: (body.user || '').trim(),
        password: body.password,
      })
      .pipe(map((response) => this.toSession(response)));
  }

  private toSession(response: ApiAuthResponse): WorkforceUserSession {
    const user = response.user;
    const role = toSessionRole(user.role);
    return {
      id: user.uuid,
      name: user.name,
      username: user.username,
      email: user.email,
      role,
      admin: role === 'Admin',
      root: role === 'Admin',
      allowedBranches: user.branchCodes ?? [],
      token: response.accessToken,
    };
  }

  saveSession(sessionData: WorkforceUserSession): void {
    try {
      sessionStorage.setItem('token', sessionData.token);
      sessionStorage.setItem('user', JSON.stringify(sessionData));
    } catch {}
    this.user$.next(sessionData);
    this.token$.next(sessionData.token);
    this._currentUser.set(sessionData);
  }

  changePassword(body: { currentPassword: string; newPassword: string }): Observable<any> {
    return this.http.put<void>(`${environment.WORKFORCE_API}auth/change-password`, body);
  }

  logOut(returnUrl?: string): void {
    try {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
    } catch {}

    this.user$.next(null);
    this.token$.next(null);
    this._currentUser.set(null);

    const safeUrl = this.sanitizeReturnUrl(returnUrl || this.router.url);
    if (safeUrl && safeUrl !== '/home') {
      this.router.navigate(['/'], { queryParams: { returnUrl: safeUrl } });
    } else {
      this.router.navigateByUrl('/');
    }
  }
}
